import React, {
    useState,
    useEffect,
    useRef
} from "react";
import Hls from 'hls.js';

import { createJWT } from "./utils";
import CurlSnippet from "./components/CurlSnippet";
import InputText from "./components/Form/InputText";
import Select from "./components/SelectDropdown";

import logo from "./images/logo.svg"

const vodFeatures = [
    {
        id: 1,
        label: "Playback",
        value: "playback"
    },
    {
        id: 2,
        label: "Thumbnail",
        value: "thumbnail"
    },
    {
        id: 3,
        label: "GIF",
        value: "gif"
    },
    {
        id: 4,
        label: "Spritesheet",
        value: "spritesheet"
    },
    {
        id: 5,
        label: "DRM License",
        value: "drmLicense"
    }
];

const dataFeatures = [
    {
        id: 1,
        label: "FP Data Video ID",
        value: "fpDataVideoId"
    },
    {
        id: 2,
        label: "FP Video Asset ID",
        value: "fpVideoAssetId"
    },
    {
        id: 3,
        label: "FP Video Playback ID",
        value: "fpVideoPlaybackId"
    },
    {
        id: 4,
        label: "FP Video Live Stream ID",
        value: "fpVideoLiveStreamId"
    },
];

const thumbnailRotation = [
    {
        id: 1,
        label: "0",
        value: "0"
    },
    {
        id: 2,
        label: "90",
        value: "90"
    },
    {
        id: 3,
        label: "180",
        value: "180"
    },
    {
        id: 4,
        label: "270",
        value: "270"
    },
];

const thumbNailFitMode = [
    {
        id: 1,
        label: "Preserve",
        value: "preserve"
    },
    {
        id: 2,
        label: "Stretch",
        value: "stretch"
    },
    {
        id: 3,
        label: "Crop",
        value: "crop"
    },
    {
        id: 4,
        label: "Pad",
        value: "pad"
    }
];

const App = () => {
    const [selectedProduct, setSelectedProduct] = useState("vod");
    const [playbackId, setPlaybackId] = useState("");
    const [signingKeyId, setSigningKeyId] = useState("");
    const [selectedFeature, setSelectedFeature] = useState("playback");
    const [dataFeature, setDataFeature] = useState("fpDataVideoId")
    const [thumbnailSpec, setThumbnailSpec] = useState({ height: "200", width: "320", time: "1", rotation: "0", fitMode: "preserve", flipVertically: false, flipHorizontally: false });
    const [gifSpec, setGifSpec] = useState({ height: "480", width: "320", start: "0", end: "10", fps: "15" });
    const [gifSrc, setGifSrc] = useState("");
    const [thumbnailSrc, setThumbnailSrc] = useState("");
    const [storyboardSrc, setStoryBoardSrc] = useState("");
    const [privateKey, setPrivateKey] = useState("");
    const [jwt, setJwt] = useState("");
    const fileInputRef = useRef(null);
    const videoRef = useRef(null);

    useEffect(() => {

        const generateJWT = async () => {

            if (privateKey) {
                try {
                    const decodedKey = atob(privateKey);
                    const iat = Math.floor(Date.now() / 1000);
                    const exp = Math.floor((Date.now() + 24 * 60 * 60 * 1000) / 1000);

                    if (selectedProduct === "vod") {
                        if (signingKeyId && playbackId) {
                            const payload = {
                                "kid": `${signingKeyId}`,
                                "aud": selectedFeature === "drmLicense" ? `drm:${playbackId}` : `media:${playbackId}`,
                                "iss": "fastpix.com",
                                "sub": "",
                                "iat": iat,
                                "exp": exp
                            };

                            const generateJWT = await createJWT(decodedKey, payload);

                            if (generateJWT) {
                                setJwt(generateJWT)
                            }
                        } else {
                            setJwt("");
                        }
                    }
                } catch (error) {
                    setJwt("");
                }
            } else {
                setJwt("");
            }
        }
        generateJWT()
    }, [playbackId, signingKeyId, privateKey, selectedProduct, selectedFeature])

    useEffect(() => {
        let hls;

        if (jwt && selectedProduct === "vod" && selectedFeature === "playback" && playbackId) {
            const videoElement = videoRef.current;

            if (Hls.isSupported() && videoElement) {
                hls = new Hls();
                hls.loadSource(`https://stream.fastpix.com/${playbackId}.m3u8?token=${jwt}`);
                hls.attachMedia(videoElement);
                hls.on(Hls.Events.MANIFEST_PARSED, () => {
                    if (videoElement.paused) {
                        videoElement.play().catch(error => {
                            console.error("Error while playing video:", error);
                        });
                    }
                });
            }
        }

        return () => {
            if (hls) {
                hls.destroy();
            }
        };
    }, [jwt, selectedProduct, selectedFeature, playbackId]);

    useEffect(() => {

        if (selectedFeature === "gif") {
            if (jwt && playbackId) {
                const customGifSource = `https://images.fastpix.com/${playbackId}/clip.gif?token=${jwt}${gifSpec?.width ? `&width=${gifSpec?.width}` : ''
                    }${gifSpec?.height ? `&height=${gifSpec?.height}` : ''
                    }${gifSpec?.start ? `&start=${gifSpec?.start}` : ''
                    }${gifSpec?.end ? `&end=${gifSpec?.end}` : ''
                    }${gifSpec?.fps ? `&fps=${gifSpec?.fps}` : ''}`;
                setGifSrc(customGifSource)
            }
        }

        if (selectedFeature === "spritesheet") {

            if (jwt && playbackId) {
                const customSpriteSheet = `https://images.fastpix.com/${playbackId}/spritesheet.jpg?token=${jwt}`;
                setStoryBoardSrc(customSpriteSheet)
            }
        }

        if (selectedFeature === "thumbnail") {

            if (jwt && playbackId) {
                const customThumbnail = `https://images.fastpix.com/${playbackId}/thumbnail.jpg?token=${jwt}&time=${thumbnailSpec?.time ? thumbnailSpec?.time : "1"}${thumbnailSpec?.width ? `&width=${thumbnailSpec?.width}` : ''}${thumbnailSpec?.height ? `&height=${thumbnailSpec?.height}` : ''
                    }${thumbnailSpec?.fitMode ? `&fit_mode=${thumbnailSpec?.fitMode}` : ''}${thumbnailSpec?.rotation && Number(thumbnailSpec?.rotation) !== 0 ? `&rotate=${thumbnailSpec?.rotation}` : ''
                    }&flip_h=${thumbnailSpec?.flipHorizontally}&flip_v=${thumbnailSpec?.flipVertically}`;
                setThumbnailSrc(customThumbnail)
            }
        }
    }, [gifSpec, playbackId, signingKeyId, jwt, selectedFeature, thumbnailSpec])

    const handleButtonClick = () => {
        fileInputRef.current.click();
    };

    const handleFileChange = (event) => {
        const files = event.target.files;

        if (files.length > 0) {
            const file = event.target.files[0];
            const reader = new FileReader();

            reader.onload = (e) => {
                const fileContent = e.target.result;

                if (fileContent) {
                    const privateKey = fileContent.split('=')[1].trim();
                    setPrivateKey(privateKey);
                }
            };

            reader.onerror = () => {
                console.error("Error reading file");
            };

            reader.readAsText(file);
        }
    };

    // check if user is Admin
    const onToggleProduct = (product) => {

        if (product) {
            setSelectedProduct(product);
        }
    };

    const handlePlayBackInputChange = (event) => {
        setPlaybackId(event.target.value)
    };

    const handleOnChangeVideo = (event) => {

        if (event?.label) {
            setSelectedFeature(event?.value)
        }
    }

    const handleOnChangeData = (event) => {

        if (event?.label) {
            setDataFeature(event?.value)
        }
    }

    const handleThumbNailInput = (event, thumbnailFeature) => {

        if (thumbnailFeature === "height") {
            setThumbnailSpec((prev) => ({ ...prev, height: event?.target?.value }))
        } else if (thumbnailFeature === "width") {
            setThumbnailSpec((prev) => ({ ...prev, width: event?.target?.value }))
        } else if (thumbnailFeature === "time") {
            setThumbnailSpec((prev) => ({ ...prev, time: event?.target?.value }))
        }
    }

    const handleThumbNailSelectDropDown = (event, name) => {

        if (name === "thumbNailRotation") {
            setThumbnailSpec((prev) => ({ ...prev, rotation: event?.value }))
        } else if (name === "thumbNailFitMode") {
            setThumbnailSpec((prev) => ({ ...prev, fitMode: event?.value }))
        }
    }

    const handleFlipCheckBox = (name) => {

        if (name === "flipHorizontally") {
            setThumbnailSpec((prev) => ({ ...prev, flipHorizontally: !thumbnailSpec.flipHorizontally }))
        } else if (name === "flipVertically") {
            setThumbnailSpec((prev) => ({ ...prev, flipVertically: !thumbnailSpec.flipVertically }))
        }
    }

    const handleGifLimits = (event, thumbnailFeature) => {

        if (thumbnailFeature === "height") {
            setGifSpec((prev) => ({ ...prev, height: Number(event?.target?.value) > 640 ? "640" : event?.target?.value }))
        } else if (thumbnailFeature === "width") {
            setGifSpec((prev) => ({ ...prev, width: Number(event?.target?.value) > 640 ? "640" : event?.target?.value }))
        } else if (thumbnailFeature === "start") {
            setGifSpec((prev) => ({ ...prev, start: event?.target?.value }))
        } else if (thumbnailFeature === "end") {
            setGifSpec((prev) => ({ ...prev, end: event?.target?.value }))
        } else if (thumbnailFeature === "fps") {
            setGifSpec((prev) => ({ ...prev, fps: event?.target?.value }))
        }
    }

    const handleSigningKeyId = (event) => {
        setSigningKeyId(event.target.value)
    }

    const handlePrivateKey = (event) => {
        const newValue = event.target.value
        setPrivateKey(newValue)
    }

    return (
        <div className="App">

            {/* Header */}
            <header className="w-full h-[84px] bg-[#FBFBFB] border-b-[1px] border-[#E2E8F0] px-[30px] py-[20px] flex items-center">
                <img src={logo} alt="FastPix" />
            </header>

            <div className="w-full h-full overflow-y-auto flex flex-col lg:flex-row">

                {/* Products Container */}
                <div className="w-full lg:max-w-[564px] lg:min-h-screen  flex-grow  bg-[#F1F5FB] px-[20px] py-[20px] gap-y-[40px]">
                    <span className="text-[#000000] text-[15px] font-normal font-inter">A utility for generating signed JSON Web Tokens for <br /> use with FastPix products.</span>

                    {/* Products Radio Button */}
                    <div className='h-[24px] w-full mt-[40px] flex flex-row items-center gap-x-[40px]'>
                        <label className='h-[24px] w-[200px] radio flex flex-row items-center gap-x-[10px]'>
                            <input type='radio' className='h-[16px] mb-[10px] radio-input w-[16px]' onChange={() => onToggleProduct("vod")} checked={selectedProduct === "vod" ? true : false} value='vod' name='role' />
                            <span className={`text-[16px] radio-text ${selectedProduct === "vod" ? "text-primary" : "text-dark"} leading-[20.6px] font-medium`}>Video Playback</span>
                        </label>
                    </div>

                    {/* playbackId Input Field */}
                    {selectedProduct === "vod" &&
                        <div className="flex flex-col mt-[40px]">
                            <div className="w-full flex justify-between items-center">
                                <span className="text-[15px] font-medium leading-[18.5px] text-dark">
                                    Playback Id
                                </span>
                                {playbackId?.length === 0 &&
                                    <span className="text-[14px] text-error text-opacity-70 font-medium leading-[18.5px]">
                                        Required
                                    </span>
                                }
                            </div>
                            <div className="mt-[10px] w-full">
                                <InputText
                                    value={playbackId}
                                    autoFocus
                                    onChange={(e) => handlePlayBackInputChange(e)}
                                    error={"playbackId"}
                                    id="playbackId"
                                />
                            </div>
                        </div>
                    }

                    {/* VOD Features Select Dropdown */}
                    {selectedProduct === "vod" &&
                        <div className="flex flex-col mt-[20px]">
                            <span className="text-[16px] font-medium leading-[18.5px] text-dark">
                                Features
                            </span>
                            <div className="mt-[10px] w-full">
                                <Select
                                    options={vodFeatures}
                                    initialOption={selectedFeature}
                                    height="46px"
                                    onChange={(event) => handleOnChangeVideo(event)}
                                />
                            </div>
                        </div>
                    }

                    {/* Data Features Select Dropdown */}
                    {selectedProduct === "data" &&
                        <div className="flex flex-col mt-[20px]">
                            <span className="text-[15px] font-medium leading-[18.5px] text-dark">
                                Features
                            </span>
                            <div className="mt-[10px] w-full">
                                <Select
                                    options={dataFeatures}
                                    initialOption={dataFeature}
                                    height="46px"
                                    onChange={(event) => handleOnChangeData(event)}
                                />
                            </div>
                        </div>
                    }

                    {/* Thumbnail feature */}
                    {selectedFeature === "thumbnail" && selectedProduct === "vod" && (
                        <div className="thumbailContainer">
                            <div className="w-full flex items-center gap-x-[10px]">

                                {/* Thumbnail Height */}
                                <div className="w-full flex flex-col mt-[20px]">
                                    <span className="text-[15px] font-medium leading-[18.5px] text-dark">
                                        Height
                                    </span>
                                    <div className="mt-[10px] w-full">
                                        <InputText
                                            value={thumbnailSpec?.height}
                                            autoFocus
                                            onChange={(e) => handleThumbNailInput(e, "height")}
                                            isNumber={true}
                                            id="thumbnailHeight"
                                        />
                                    </div>
                                </div>

                                {/* Thumbnail Width */}
                                <div className="w-full flex flex-col mt-[20px]">
                                    <span className="text-[15px] font-medium leading-[18.5px] text-dark">
                                        Width
                                    </span>

                                    <div className="mt-[10px] w-full">
                                        <InputText
                                            value={thumbnailSpec?.width}
                                            autoFocus
                                            onChange={(e) => handleThumbNailInput(e, "width")}
                                            isNumber={true}
                                            id="thumbnailWidth"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="w-full flex gap-x-[20px] items-center">

                                {/* Thumbnail Time */}
                                <div className="w-full flex flex-col mt-[20px]">
                                    <span className="text-[15px] font-medium leading-[18.5px] text-dark">
                                        Time
                                    </span>
                                    <div className="mt-[10px] w-full">
                                        <InputText
                                            value={thumbnailSpec?.time}
                                            autoFocus
                                            onChange={(e) => handleThumbNailInput(e, "time")}
                                            isNumber={true}
                                            id="thumbnailTime"
                                        />
                                    </div>
                                </div>

                                {/* Thumbnail Rotation */}
                                <div className="w-full flex flex-col mt-[20px]">
                                    <span className="text-[15px] font-medium leading-[18.5px] text-dark">
                                        Rotation
                                    </span>
                                    <div className="w-full mt-[10px]">
                                        <Select
                                            options={thumbnailRotation}
                                            initialOption={thumbnailSpec?.rotation}
                                            height="46px"
                                            onChange={(event) => handleThumbNailSelectDropDown(event, "thumbNailRotation")}
                                            isNumber={true}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Thumbnail Fitmode */}
                            <div className="flex flex-col mt-[20px]">
                                <span className="text-[15px] font-medium leading-[18.5px] text-dark">
                                    Fit Mode
                                </span>
                                <div className="mt-[10px] w-full">
                                    <Select
                                        options={thumbNailFitMode}
                                        initialOption={thumbnailSpec?.fitMode}
                                        height="46px"
                                        onChange={(event) => handleThumbNailSelectDropDown(event, "thumbNailFitMode")}
                                    />
                                </div>
                            </div>

                            {/* Thumbnail Flip Horizontal & Flip Vertical */}
                            <div className="w-full flex items-center mt-[40px]">
                                <div className="w-full flex items-center gap-x-[16px]">
                                    <span className="text-[15px] text-dark font-medium leading-[19px]">Flip Vertically</span>
                                    <label className="relative inline-flex items-center cursor-pointer focus:outline-none focus:shadow-none active:outline-none">
                                        <input id="flipVertically" type="checkbox" className="focus:outline-none focus:shadow-none sr-only peer focus:outline-offset-0 focus:ring-0 " checked={thumbnailSpec?.flipVertically} onChange={() => handleFlipCheckBox("flipVertically")} />
                                        <div className="w-11 h-6 bg-unchecked peer-focus:outline-none focus:ring-0 rounded-full peer peer-checked:after:translate-x-full peer-checked:before:border-white peer-checked:after:border-white after:content-[''] after:outline-none before:outline-none after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:border-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-checked">
                                        </div>
                                    </label>
                                </div>
                                <div className="w-full flex items-center gap-x-[16px]">
                                    <span className="text-[15px] text-dark font-medium leading-[19px]">Flip Horizontally</span>
                                    <label className="relative inline-flex outline-none items-center cursor-pointer">
                                        <input id="flipHorizontally" type="checkbox" className="focus:outline-none focus:shadow-none focus:border-none focus:ring-0 " checked={thumbnailSpec?.flipHorizontally} onChange={() => handleFlipCheckBox("flipHorizontally")} className="sr-only peer" />
                                        <div className="w-11 h-6 bg-unchecked peer-focus:outline-none  focus:ring-0  rounded-full peer peer-checked:after:translate-x-full peer-checked:before:border-white peer-checked:after:border-white after:content-[''] peer-checked:after:outline-none peer-checked:before:outline-none after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:border-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-checked">
                                        </div>
                                    </label>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* GIF Feature */}
                    {selectedFeature === "gif" && selectedProduct === "vod" && (
                        <div className="gifContainer">
                            <div className="w-full flex items-center gap-x-[10px]">

                                {/* GIF Start Time*/}
                                <div className="w-full flex flex-col mt-[20px]">
                                    <span className="text-[15px] font-medium leading-[18.5px] text-dark">
                                        Start
                                    </span>
                                    <div className="mt-[10px] w-full">
                                        <InputText
                                            value={gifSpec?.start}
                                            autoFocus
                                            onChange={(e) => handleGifLimits(e, "start")}
                                            isNumber={true}
                                            id="gifStart"
                                        />
                                    </div>
                                </div>

                                {/* GIF End Time */}
                                <div className="w-full flex flex-col mt-[20px]">
                                    <span className="text-[15px] font-medium leading-[18.5px] text-dark">
                                        End
                                    </span>
                                    <div className="mt-[10px] w-full">
                                        <InputText
                                            value={gifSpec?.end}
                                            autoFocus
                                            onChange={(e) => handleGifLimits(e, "end")}
                                            isNumber={true}
                                            id="gifEnd"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="w-full flex gap-x-[10px] items-center">

                                {/* GIF Height */}
                                <div className="w-full flex flex-col mt-[20px]">
                                    <span className="text-[15px] font-medium leading-[18.5px] text-dark">
                                        Height
                                    </span>
                                    <div className="mt-[10px] w-full">
                                        <InputText
                                            value={gifSpec?.height}
                                            autoFocus
                                            onChange={(e) => handleGifLimits(e, "height")}
                                            isNumber={true}
                                            id="gifHeight"
                                        />
                                    </div>
                                </div>

                                {/* GIF Width */}
                                <div className="w-full flex flex-col mt-[20px]">
                                    <span className="text-[15px] font-medium leading-[18.5px] text-dark">
                                        Width
                                    </span>
                                    <div className="mt-[10px] w-full">
                                        <InputText
                                            value={gifSpec?.width}
                                            autoFocus
                                            onChange={(e) => handleGifLimits(e, "width")}
                                            isNumber={true}
                                            id="gifWidth"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* GIF FPS */}
                            <div className="flex flex-col mt-[20px]">
                                <span className="text-[15px] font-medium leading-[18.5px] text-dark">
                                    FPS
                                </span>
                                <div className="mt-[10px] w-full">
                                    <InputText
                                        value={gifSpec?.fps}
                                        autoFocus
                                        onChange={(e) => handleGifLimits(e, "fps")}
                                        isNumber={true}
                                        id="gifFps"
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                    <div className="w-full flex flex-col mt-[40px]">
                        <span className="text-[16px] font-semibold leading-[18.5px] text-dark">
                            Expiration
                        </span>
                        <span className="text-[14px] font-medium leading-[18.5px] mt-[10px] text-dark">
                            This tool provides a token that is valid for 24 hours.
                        </span>
                    </div>
                    <div className="w-full mt-[40px]">
                        <span className="text-[16px] font-semibold leading-[18.5px] text-dark">
                            Payload
                        </span>
                        <div className="mt-[10px]">
                            <CurlSnippet product={selectedProduct} playbackId={playbackId} signingKeyId={signingKeyId} selectedFeature={selectedFeature} />
                        </div>
                    </div>
                </div>

                {/* Signing key Container */}
                <div className="w-full lg:min-h-screen bg-[#FBFBFB] flex-grow overflow-y-auto px-[20px] py-[20px]">
                    <div className="min-h-[56px] w-full rounded-[8px] border-[1px] border-[#ED9902] p-[10px] flex flex-col justify-center  bg-[#FFFAE6]">
                        <span className="text-dark text-[15px] leading-[19px]">
                            Do not ship JWTs in your app. This signer is meant to help develop an app, but is not a replacement for authentication in production.
                        </span>
                    </div>

                    {/* Input Signingkey Id */}
                    <div className="flex flex-col mt-[40px]">
                        <div className="w-full flex justify-between items-center">
                            <span className="text-[15px] font-medium leading-[18.5px] text-dark">
                                Signing Key ID
                            </span>

                            {signingKeyId?.length === 0 &&
                                <span className="text-[14px] text-error text-opacity-70 font-medium leading-[18.5px]">
                                    Required
                                </span>
                            }
                        </div>
                        <div className="mt-[10px] w-full">
                            <InputText
                                value={signingKeyId}
                                autoFocus
                                onChange={(e) => handleSigningKeyId(e)}
                                error={"signingKeyId"}
                                id="signingKeyId"
                            />
                        </div>
                    </div>

                    {/* Private Key */}
                    <div className="flex flex-col mt-[40px]">
                        <div className="w-full flex items-center gap-x-[15px]">
                            <div className="w-full flex flex-col">
                                <div className="w-full flex justify-between items-center">
                                    <span className="text-[15px] font-medium leading-[18.5px] text-dark">
                                        Secret
                                    </span>

                                    {privateKey?.length === 0 &&
                                        <span className="text-[14px] text-error text-opacity-70 font-medium leading-[18.5px]">
                                            Required
                                        </span>
                                    }
                                </div>
                                <div className="mt-[10px]">
                                    <InputText
                                        value={privateKey}
                                        autoFocus
                                        width="647px"
                                        onChange={(e) => handlePrivateKey(e)}
                                        error={"secret"}
                                        id="secret"
                                    />
                                </div>
                            </div>
                            <div className="text-[16px] mt-[28px] text-[#333333] leaading-[20px] font-normal">
                                OR
                            </div>
                            <div className="mt-[28px]">
                                <button
                                    type="button"
                                    onClick={handleButtonClick}
                                    className="h-[40px] w-[82px] bg-[#FFFFFF] border-[1px] border-[#050505] rounded-[4px]"
                                >
                                    .env file
                                </button>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    onChange={handleFileChange}
                                    accept=".env"
                                    style={{ display: "none" }}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="w-full mt-[40px]">
                        <CurlSnippet jwt={jwt} heading="Signed JWT" />
                    </div>
                    {selectedProduct === "vod" && jwt && selectedFeature !== "drmLicense" &&
                        (
                            <div className="w-full mt-[40px]">
                                <div className="w-full h-[52px] bg-[#20304A] text-white-1 text-[15px] font-medium px-[15px] py-[10px] flex items-center rounded-tl-[10px] rounded-tr-[10px]">
                                    {selectedFeature === "playback" ? "Playback" : selectedFeature === "gif" ? "GIF" : selectedFeature === "thumbnail" ? "Thumbnail" : selectedFeature === "spritesheet" ? "Spritesheet" : selectedFeature === "drmLicense" ? "DRM License" : ""}
                                </div>
                                <div className="w-full px-[20px] bg-[#11223D] py-[20px] rounded-bl-[10px] rounded-br-[10px]">
                                    {selectedFeature === "playback" && (
                                        <video
                                            ref={videoRef}
                                            height="200px"
                                            controls
                                            autoPlay
                                            muted
                                            className="w-full"
                                        />
                                    )}
                                    {
                                        selectedFeature === "gif" && gifSrc && (
                                            <img src={gifSrc} alt="Gif" className="text-white-1 text-[14px] font-medium" />
                                        )
                                    }
                                    {
                                        selectedFeature === "spritesheet" && storyboardSrc && (
                                            <img src={storyboardSrc} alt="Spritesheet" className="text-white-1 text-[14px]  font-medium" />
                                        )
                                    }
                                    {
                                        selectedFeature === "thumbnail" && thumbnailSrc && (
                                            <img src={thumbnailSrc} alt="Thumbnail" className="text-white-1 text-[14px] font-medium" />
                                        )
                                    }
                                </div>
                            </div>
                        )
                    }
                </div>
            </div>
        </div>
    )
}

export default App
